import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api-dev.oshudstock.com';
const TOKEN_KEY = 'admin_session_token';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On app load, check if we have a token and fetch the session
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      fetchSession(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchSession = async (token) => {
    try {
      const res = await axios.get(`${API_BASE}/api/auth/get-session`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.data?.user) {
        setUser(res.data.data.user);
      } else {
        // Token is expired or invalid
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      }
    } catch (err) {
      console.error('Session fetch failed:', err);
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API_BASE}/api/auth/sign-in/email`, {
      email,
      password
    });

    // better-auth returns token in the response body when using bearer mode
    const token = res.data?.token || res.data?.data?.token;
    const userData = res.data?.user || res.data?.data?.user;

    if (!token) {
      throw new Error('No token returned from server');
    }

    localStorage.setItem(TOKEN_KEY, token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      await axios.post(`${API_BASE}/api/auth/sign-out`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.warn('Sign out request failed:', err);
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const getToken = () => localStorage.getItem(TOKEN_KEY);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
